import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    TablePagination,
    Chip,
    Stack,
    Avatar,
    Container,
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Paper
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CustomButton from "../../components/common/CustomButton";
import { useLocation } from "react-router-dom";
import { jdApi } from "../../api/jobdescriptionApi";
import { candidateApi } from "../../api/candidateApi";
import { ERROR_MESSAGES, getPlanExhaustMessage } from "../../components/common/StatusDictionary";
import CustomTable from "../../components/common/CustomTable";
import CustomFilter from "../../components/common/CustomFilter";
import { formatDate, formatTime } from "../../utils/dateUtils";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
} from "recharts";
import CustomSnackBar from "../../components/common/CustomSnackbar";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomHeader from "../../components/common/CustomHeader";
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

export const candidateFilters = [
    {
        key: "overall_score",
        label: "Overall Score",
        type: "range",
        min: 0,
        max: 100,
        step: 1,
    },
    {
        key: "final_verdict",
        label: "Verdict",
        type: "checkbox",
        options: [
            { label: "Strong Fit", value: "Strong Fit" },
            { label: "Moderate Fit", value: "Moderate Fit" },
            { label: "Weak Fit", value: "Weak Fit" },
        ],
    },
    {
        key: "candidate_status",
        label: "Candidate Status",
        type: "checkbox",
        options: [
            { label: "New", value: "NEW" },
            { label: "Scheduled", value: "SCHEDULED" },
            { label: "In Progress", value: "IN PROGRESS" },
            { label: "Attented", value: "ATTENDED" },
            { label: "Not Attended", value: "NOT ATTENDED" },
            { label: "Interrupted", value: "INTERRUPTED" },
        ],
    },
];

// Reusable tooltip styling
const useTooltipProps = (theme) => ({
    arrow: true,
    componentsProps: {
        tooltip: {
            sx: {
                bgcolor: theme.palette.text.primary,
                color: theme.palette.background.paper,
                fontWeight: 600,
                fontSize: "0.7rem",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                p: "6px 12px",
                borderRadius: "8px",
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
            },
        },
        arrow: {
            sx: {
                color: theme.palette.text.primary,
            }
        }
    },
});

const ResumeMatcherLLMPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const ttProps = useTooltipProps(theme);

    const STATUS_CONFIG = {
        NEW: { bg: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main, border: alpha(theme.palette.primary.main, 0.3) },
        SCHEDULED: { bg: alpha(theme.palette.secondary.main, 0.15), color: theme.palette.secondary.main, border: alpha(theme.palette.secondary.main, 0.3) },
        RESCHEDULED: { bg: alpha(theme.palette.secondary.main, 0.15), color: theme.palette.secondary.main, border: alpha(theme.palette.secondary.main, 0.3) },
        INPROGRESS: { bg: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.main, border: alpha(theme.palette.warning.main, 0.3) },
        TERMINATED: { bg: alpha(theme.palette.error.main, 0.15), color: theme.palette.error.main, border: alpha(theme.palette.error.main, 0.3) },
        COMPLETED: { bg: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.main, border: alpha(theme.palette.success.main, 0.3) },
        INTERRUPTED: { bg: alpha(theme.palette.error.main, 0.15), color: theme.palette.error.main, border: alpha(theme.palette.error.main, 0.3) },
        NOT_ATTENDED: { bg: alpha(theme.palette.error.main, 0.15), color: theme.palette.error.main, border: alpha(theme.palette.error.main, 0.3) },
    };

    const location = useLocation();
    const jdId = location.state?.jdId;

    const [jdTitle, setJdTitle] = useState("");
    const [resumeLoadingId, setResumeLoadingId] = useState(null);

    const [candidates, setCandidates] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [loadingCandidateId, setLoadingCandidateId] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [reInviteOpen, setReInviteOpen] = useState(false);
    const [reInviteComment, setReInviteComment] = useState("");

    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenReInvite = (row) => {
        if ((row.status || "").toUpperCase() === "NEW" || (row.status || "").toUpperCase() === "COMPLETED") {
            setSnackbar({ open: true, message: "Cannot re-invite NEW or COMPLETED candidates", type: "warning" });
            handleMenuClose();
            return;
        }
        setReInviteComment("");
        setReInviteOpen(true);
        handleMenuClose();
    };

    const handleReInviteSubmit = async () => {
        if (!reInviteComment.trim()) {
            setSnackbar({ open: true, message: "Comment is mandatory", type: "warning" });
            return;
        }
        setInviteLoading(true);
        try {
            const payload = {
                jd_id: jdId,
                candidate:
                {
                    cand_id: menuRow.id,
                    cand_name: menuRow.name,
                    email: menuRow.email,
                    agg_score: menuRow.agg_score,
                    cand_status: menuRow.status,
                },
                comment: reInviteComment,
            };
            const response = await candidateApi.resendInvitation(payload);
            // console.log("reinvite response", response)
            if (response.status === 200) {
                setSnackbar({ open: true, message: response.message || "Invitation resent successfully", type: "success" });
                setReInviteOpen(false);
                fetchData();
            } else if (response.status === 403) {
                const errorMessage = getPlanExhaustMessage(response.message) || response.message || "Failed to resend invitation";
                setSnackbar({ open: true, message: errorMessage, type: "error" });
            } else {
                setSnackbar({ open: true, message: response.message || "Failed to resend invitation", type: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: "Error resending invitation", type: "error" });
        } finally {
            setInviteLoading(false);
        }
    };

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        type: "",
    });

    const getStatusStyle = (status) =>
        STATUS_CONFIG[status] || { bg: alpha(theme.palette.text.secondary, 0.1), color: theme.palette.text.secondary, border: theme.palette.divider };

    const [filters, setFilters] = useState([{
        property: "jd_id",
        value: jdId,
    }]);

    const values = useMemo(() => {
        const obj = {};
        filters.forEach(({ property, value, operator }) => {
            if (operator) {
                obj[property] = { value: value?.value ?? value, operator };
            } else if (Array.isArray(obj[property])) {
                obj[property].push(value);
            } else if (!obj[property]) {
                obj[property] = [value];
            } else {
                obj[property] = [obj[property], value];
            }
        });
        return obj;
    }, [filters]);

    const handleChange = (key, value, mode, operator = "equal") => {
        if (key === "jd_id") return;

        setFilters((prev) => {
            let updatedFilters = [...prev];

            if (mode === "single") {
                updatedFilters = updatedFilters.filter((f) => f.property !== key);
                updatedFilters.push({ property: key, value, operator });
            } else {
                const exists = updatedFilters.some((f) => f.property === key && f.value === value);
                if (exists) {
                    updatedFilters = updatedFilters.filter((f) => !(f.property === key && f.value === value));
                } else {
                    updatedFilters.push({ property: key, value });
                }
            }

            const hasJdId = updatedFilters.some((f) => f.property === "jd_id");
            if (!hasJdId && jdId) {
                updatedFilters.push({ property: "jd_id", value: jdId });
            }
            return updatedFilters;
        });
    };

    const columns = [
        {
            field: "candidate",
            label: "Candidate",
            render: (_, row) => {
                const status = (row.status || "NEW").toUpperCase();
                const style = getStatusStyle(status);

                return (
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
                        <Avatar sx={{
                            width: 38,
                            height: 38,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 800,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}>
                            {(row.name || "?")[0]?.toUpperCase()}
                        </Avatar>

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                                <Typography fontWeight={700} fontSize="0.88rem" fontFamily="'Plus Jakarta Sans', sans-serif">
                                    {row.name || "Unknown"}
                                </Typography>

                                <Chip
                                    label={status}
                                    size="small"
                                    sx={{
                                        fontSize: "0.6rem",
                                        fontWeight: 800,
                                        bgcolor: style.bg,
                                        color: style.color,
                                        border: `1px solid ${style.border}`,
                                        height: 20,
                                        borderRadius: '6px'
                                    }}
                                />
                            </Stack>

                            <Typography fontSize="0.75rem" color="text.secondary" fontFamily="'Plus Jakarta Sans', sans-serif">
                                {row.email || "-"}
                            </Typography>
                        </Box>
                    </Stack>
                );
            },
        },
        {
            field: "overall_score",
            label: "Overall",
            render: (_, row) => {
                const overall = Math.round(row.agg_score || 0);
                const data = [
                    { name: "Score", value: overall },
                    { name: "Remaining", value: 100 - overall },
                ];

                const getScoreColor = (val) => {
                    if (val >= 80) return theme.palette.success.main;
                    if (val >= 50) return theme.palette.warning.main;
                    return theme.palette.error.main;
                };

                const scoreColor = getScoreColor(overall);

                return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 50, height: 50, position: 'relative' }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={data}
                                        innerRadius={16}
                                        outerRadius={24}
                                        startAngle={90}
                                        endAngle={-270}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        <Cell fill={scoreColor} />
                                        <Cell fill={alpha(theme.palette.text.primary, 0.1)} />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <Box sx={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Typography
                                    fontSize="0.65rem"
                                    fontWeight="800"
                                    color={theme.palette.text.primary}
                                    fontFamily="'Plus Jakarta Sans', sans-serif"
                                >
                                    {overall}%
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                );
            },
        },
        {
            field: "skills",
            label: "Skills",
            render: (_, r) => (
                <Typography fontSize="0.8rem" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">
                    {r.breakdown?.skills ?? 0}%
                </Typography>
            ),
        },
        {
            field: "experience",
            label: "Experience",
            render: (_, r) => (
                <Typography fontSize="0.8rem" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">
                    {r.breakdown?.experience ?? 0}%
                </Typography>
            ),
        },
        {
            field: "qualification",
            label: "Qualification",
            render: (_, r) => (
                <Typography fontSize="0.8rem" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">
                    {r.breakdown?.qualification ?? 0}%
                </Typography>
            ),
        },
        {
            field: "verdict",
            label: "Verdict1",
            render: (_, row) => {
                const verdict = row.verdict || "Unknown";
                let color = theme.palette.text.secondary;
                let bg = alpha(theme.palette.text.secondary, 0.1);
                let border = alpha(theme.palette.text.secondary, 0.2);

                if (verdict.toLowerCase().includes("strong")) {
                    color = theme.palette.success.main;
                    bg = alpha(theme.palette.success.main, 0.15);
                    border = alpha(theme.palette.success.main, 0.3);
                } else if (verdict.toLowerCase().includes("moderate")) {
                    color = theme.palette.warning.main;
                    bg = alpha(theme.palette.warning.main, 0.15);
                    border = alpha(theme.palette.warning.main, 0.3);
                } else if (verdict.toLowerCase().includes("weak")) {
                    color = theme.palette.error.main;
                    bg = alpha(theme.palette.error.main, 0.15);
                    border = alpha(theme.palette.error.main, 0.3);
                }

                return (
                    <Tooltip title={row.reason || "No explanation provided"} placement="top" {...ttProps}>
                        <Chip
                            label={verdict}
                            size="small"
                            sx={{
                                fontSize: "0.65rem",
                                fontWeight: 800,
                                bgcolor: bg,
                                color: color,
                                border: `1px solid ${border}`,
                                height: 24,
                                borderRadius: '8px',
                                cursor: "pointer",
                                fontFamily: "'Plus Jakarta Sans', sans-serif"
                            }}
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: "scheduled_at",
            label: "Scheduled At",
            render: (_, row) => {
                if (!row.scheduled_at) return <Typography fontSize="0.8rem" color="text.secondary">—</Typography>;
                return (
                    <Typography fontSize="0.8rem" fontWeight={500} fontFamily="'Plus Jakarta Sans', sans-serif">
                        {formatDate(row.scheduled_at)}{" "}
                        <span style={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
                            {formatTime(row.scheduled_at)}
                        </span>
                    </Typography>
                );
            },
        },
        {
            field: "expired_at",
            label: "Expired At",
            render: (_, row) => {
                if (!row.expired_at) return <Typography fontSize="0.8rem" color="text.secondary">—</Typography>;
                return (
                    <Typography fontSize="0.8rem" fontWeight={500} fontFamily="'Plus Jakarta Sans', sans-serif">
                        {formatDate(row.expired_at)}{" "}
                        <span style={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
                            {formatTime(row.expired_at)}
                        </span>
                    </Typography>
                );
            },
        },
        {
            field: "actions",
            label: "Actions",
            render: (_, row) => (
                <IconButton
                    onClick={(e) => handleMenuOpen(e, row)}
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        borderRadius: '8px',
                        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) }
                    }}
                    size="small"
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await jdApi.getJDMatchResults(jdId);
            const { status, message, data } = response || {};

            if (status === 0) {
                setSnackbar({ open: true, message: "Network error. Please check your connection.", type: "warning" });
                return;
            }

            if (status >= 400) {
                setSnackbar({ open: true, message: message || "Failed to fetch JD details.", type: "error" });
                return;
            }

            if (status === 200 && data) {
                setJdTitle(data.job_title || "");
                const list = (data.candidate_matches || []).map((candidate) => {
                    const skills = candidate.skills_score ?? 0;
                    const experience = candidate.experience_score ?? 0;
                    const qualification = candidate.education_score ?? 0;

                    return {
                        id: candidate.candidate_id,
                        name: candidate.name || "Unknown",
                        email: candidate.email || "-",
                        breakdown: { skills, experience, qualification },
                        agg_score: candidate.overall_score ?? 0,
                        status: candidate.candidate_status || "NEW",
                        verdict: candidate.final_verdict,
                        reason: candidate.reason,
                        scheduled_at: candidate.interview_startAt ?? null,
                        expired_at: candidate.interview_expireAt ?? null,
                    };
                });
                setCandidates(list);
                return;
            }
            setSnackbar({ open: true, message: "Unexpected response from server.", type: "error" });
        } catch (err) {
            console.error("Error fetching JD details:", err);
            setSnackbar({ open: true, message: "Something went wrong", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        fetchData();
        setFilters([]);
    };

    const handleApply = async () => {
        try {
            const response = await jdApi.getJDMatchFilter({ "filter": filters });
            const list = (response.data || []).map((candidate) => {
                const skills = candidate.skills_score ?? 0;
                const experience = candidate.experience_score ?? 0;
                const qualification = candidate.education_score ?? 0;

                return {
                    id: candidate.candidate_id,
                    name: candidate.name || "Unknown",
                    email: candidate.email || "-",
                    breakdown: { skills, experience, qualification },
                    agg_score: candidate.overall_score ?? 0,
                    status: candidate.candidate_status || "NEW",
                    verdict: candidate.final_verdict,
                    reason: candidate.reason,
                    scheduled_at: candidate.interview_startAt ?? null,
                    expired_at: candidate.interview_expireAt ?? null,
                };
            });
            setCandidates(list);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (jdId) fetchData();
    }, [jdId]);

    const handleSearch = (value) => {
        setSearchValue(value);
        if (!value) {
            setCandidates(candidates);
            return;
        }
        const lower = value.toLowerCase();
        const filtered = candidates.filter((item) => {
            return (
                item.name?.toLowerCase().includes(lower) ||
                item.email?.toLowerCase().includes(lower) ||
                item.skills?.join(" ").toLowerCase().includes(lower)
            );
        });
        setCandidates(filtered);
    };

    const handleInvite = async (selectedCand = null) => {
        let payload = null;
        let inviteCount = 0;

        try {
            if (selectedCand && (selectedCand.status || "").toUpperCase() === "NEW") {
                setLoadingCandidateId(selectedCand.id);
                payload = {
                    isAlone: true,
                    jd_details: { jd_id: jdId },
                    candidate_details: [
                        {
                            cand_id: selectedCand.id,
                            cand_name: selectedCand.name,
                            email: selectedCand.email,
                            agg_score: selectedCand.agg_score,
                            cand_status: selectedCand.status,
                        },
                    ],
                };
                inviteCount = 1;
            } else {
                setInviteLoading(true);
                const toInvite = candidates.filter((c) => (c.status || "").toUpperCase() === "NEW");

                if (toInvite.length === 0) {
                    setSnackbar({ open: true, message: "Candidate not eligible: status is not New or score below threshold.", type: "warning" });
                    setInviteLoading(false);
                    return;
                }

                payload = {
                    isAlone: false,
                    jd_details: { jd_id: jdId },
                    candidate_details: toInvite.map((c) => ({
                        cand_id: c.id,
                        cand_name: c.name,
                        email: c.email,
                        agg_score: c.agg_score,
                        cand_status: c.status,
                    })),
                };
                inviteCount = toInvite.length;
            }
            const response = await candidateApi.getInvitations(payload);
            const { status, data, message } = response || {};

            if (status === 200) {
                setSnackbar({ open: true, message: message || `${inviteCount} Invitation(s) sent!`, type: "success" });
                fetchData();
                return;
            } else if (status === 403 && data["message"]) {
                const errorMessage = getPlanExhaustMessage(data["message"]) || data["message"] || "Invitation failed.";
                setSnackbar({ open: true, message: errorMessage, type: "error" });
                return;
            }
        } catch (err) {
            console.error("Invitation API error:", err);
            const errorCode = err?.response?.data?.message;
            const errorMessage = getPlanExhaustMessage(errorCode) || errorCode || "Failed to send invitations.";
            setSnackbar({ open: true, message: errorMessage, type: "error" });
        } finally {
            setLoadingCandidateId(null);
            setInviteLoading(false);
        }
    };

    const handleViewResume = async (candidate_id) => {
        try {
            setResumeLoadingId(candidate_id);
            const response = await candidateApi.viewResume({ candidate_id });
            const { status, data } = response;

            if (status === 200 && data) {
                window.open(data.download_url, "_blank");
            }
        } catch (err) {
            console.error("Resume view error:", err);
            setSnackbar({ open: true, message: "Failed to open resume.", type: "error" });
        } finally {
            setResumeLoadingId(null);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 3, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <SkeletonLoader type="jd-analysis" rows={6} />
            </Container>
        );
    }

    return (
        <Box sx={{
            minHeight: "70vh",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            pb: 4
        }}>
            <Box sx={{ mb: 2 }}>
                <CustomHeader title="Candidate Matches12" />
            </Box>

            <Box sx={{ px: { xs: 1, md: 2 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: "16px",
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: isDark
                            ? "0 8px 32px -10px rgba(0,0,0,0.6)"
                            : "0 8px 24px -10px rgba(15, 23, 42, 0.06)",
                        background: isDark ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                        backdropFilter: "blur(12px)",
                        overflow: "hidden",
                        position: "relative"
                    }}
                >
                    {/* Decorative Blob */}
                    <Box sx={{
                        position: "absolute",
                        top: -40, right: -40,
                        width: 150, height: 150,
                        background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                        borderRadius: "50%",
                        pointerEvents: "none",
                        zIndex: 0
                    }} />

                    {/* JD Header Bar */}
                    <Box sx={{
                        p: 2.5,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                    }}>
                        <Typography fontWeight={800} fontSize="1.15rem" color="text.primary" sx={{ whiteSpace: "nowrap" }}>
                            {jdTitle}
                        </Typography>

                        <Box sx={{ flex: 1, minWidth: 250, display: 'flex', justifyContent: 'flex-end' }}>
                            <CustomFilter
                                filtersConfig={candidateFilters}
                                values={values}
                                onChange={handleChange}
                                onApply={handleApply}
                                onClear={handleClear}
                                onSearch={handleSearch}
                                searchValue={searchValue}
                            />
                        </Box>

                        <CustomButton
                            variant="contained"
                            startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleInvite()}
                            disabled={inviteLoading || candidates.filter(c => (c.status || "").toUpperCase() === "NEW").length === 0}
                            sx={{
                                height: 38,
                                minWidth: 120,
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                borderRadius: "8px",
                                textTransform: "none",
                                fontFamily: "inherit",
                                whiteSpace: "nowrap",
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
                                }
                            }}
                        >
                            {inviteLoading ? "Inviting..." : "Invite All"}
                        </CustomButton>
                    </Box>

                    {/* Candidate Table Area */}
                    <Box sx={{ width: '100%', position: 'relative', zIndex: 1 }}>
                        <CustomTable
                            columns={columns}
                            rows={candidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                            dense
                        />
                    </Box>

                    {/* Pagination */}
                    <Box sx={{
                        px: 2,
                        py: 1,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        bgcolor: alpha(theme.palette.background.default, 0.4)
                    }}>
                        <TablePagination
                            component="div"
                            count={candidates.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontWeight: 500,
                                    fontSize: "0.8rem"
                                }
                            }}
                        />
                    </Box>
                </Paper>
            </Box>

            <CustomSnackBar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.type}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            />

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.8)" : "0 4px 20px rgba(0,0,0,0.1)",
                        border: `1px solid ${theme.palette.divider}`,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        minWidth: 140,
                    }
                }}
            >
                {(menuRow?.status || "").toUpperCase() === "NEW" ? (
                    <MenuItem onClick={() => { handleInvite(menuRow); handleMenuClose(); }} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        Invite
                    </MenuItem>
                ) : (
                    <MenuItem onClick={() => handleOpenReInvite(menuRow)} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        Re-Invite
                    </MenuItem>
                )}
                <MenuItem onClick={() => { handleViewResume(menuRow?.id); handleMenuClose(); }} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    {resumeLoadingId === menuRow?.id ? "Opening..." : "View Resume"}
                </MenuItem>
            </Menu>

            {/* Re-Invite Dialog (styled exactly like SkillsModal) */}
            <Dialog
                open={reInviteOpen}
                onClose={() => setReInviteOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        overflow: "hidden",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        boxShadow: isDark ? "0 20px 40px rgba(0,0,0,0.6)" : "0 20px 40px -10px rgba(0, 0, 0, 0.2)",
                        bgcolor: theme.palette.background.paper,
                    },
                }}
            >
                <Box sx={{
                    background: isDark
                        ? theme.palette.background.default
                        : `linear-gradient(135deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[900]} 100%)`,
                    px: 2.5, pt: 2.5, pb: 2,
                    position: "relative",
                    overflow: "hidden",
                }}>
                    <Box sx={{
                        position: "absolute",
                        top: -20, right: -10,
                        width: 70, height: 70,
                        background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.3)} 0%, transparent 70%)`,
                        borderRadius: "50%",
                    }} />

                    <IconButton
                        size="small"
                        onClick={() => setReInviteOpen(false)}
                        sx={{
                            position: "absolute", top: 10, right: 10,
                            color: alpha("#fff", 0.6),
                            "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.1) },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>

                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#fff", fontFamily: "inherit" }}>
                        Re-Invite Candidate
                    </Typography>
                </Box>

                <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2, bgcolor: theme.palette.background.paper }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500, fontFamily: "inherit" }}>
                        Please provide a mandatory comment/reason for re-inviting this candidate.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Enter your comment here..."
                        value={reInviteComment}
                        onChange={(e) => setReInviteComment(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                bgcolor: theme.palette.background.default,
                                fontSize: "0.85rem",
                                fontFamily: "inherit",
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{
                    px: 2.5, py: 2,
                    bgcolor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}>
                    <CustomButton
                        variant="outlined"
                        onClick={() => setReInviteOpen(false)}
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: "0.8rem", fontFamily: "inherit" }}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="contained"
                        onClick={handleReInviteSubmit}
                        disabled={inviteLoading || !reInviteComment.trim()}
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: "0.8rem", fontFamily: "inherit" }}
                    >
                        {inviteLoading ? "Sending..." : "Re-Invite"}
                    </CustomButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ResumeMatcherLLMPage;